import { useRef } from "react";
import { Upload, X, Paperclip } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const functionalityOptions: Record<string, string[]> = {
  "Framer Website Design & Development": [
    "CMS Integration",
    "Animations & Interactions",
    "SEO Structure",
    "Blog / Editorial",
    "Multi-language",
    "Contact Form",
    "Analytics Setup",
  ],
  "Framer Landing Page": [
    "Animations & Interactions",
    "SEO Setup",
    "Contact Form",
    "A/B Testing",
    "Analytics",
  ],
  "Framer E-Commerce Development": [
    "Product Pages",
    "Cart & Checkout",
    "Framer Commerce / Frameship",
    "Shopify Backend",
    "Inventory Sync",
    "Shipping Setup",
    "Payment Integration",
  ],
  "Email Marketing Design · Klaviyo": [
    "Flow Automation",
    "Template Design",
    "Segmentation",
    "Campaign Calendar",
    "A/B Testing",
    "Analytics Setup",
  ],
  "Product Design (UX/UI)": [
    "User Research",
    "Wireframing",
    "Prototyping",
    "Design System",
    "Usability Testing",
  ],
  "Website Design Assessment": [
    "UX Audit",
    "Performance Review",
    "SEO Analysis",
    "Accessibility Check",
    "Competitor Benchmarking",
  ],
};

interface StepFunPartProps {
  message: string;
  links: string;
  files: File[];
  desiredFeatures: string[];
  projectType: string;
  update: (field: string, value: string) => void;
  setFiles: (files: File[]) => void;
  setDesiredFeatures: (features: string[]) => void;
  isDallasLocal: boolean;
  dallasOfferType: string;
  tradeOffer: string;
  setTradeOffer: (v: string) => void;
}

const MAX_FILES = 3;
const MAX_SIZE_MB = 10;

const StepFunPart = ({
  message,
  links,
  files,
  desiredFeatures,
  projectType,
  update,
  setFiles,
  setDesiredFeatures,
  isDallasLocal,
  dallasOfferType,
  tradeOffer,
  setTradeOffer,
}: StepFunPartProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const chips = functionalityOptions[projectType] || [];

  const toggleFeature = (feat: string) => {
    setDesiredFeatures(
      desiredFeatures.includes(feat)
        ? desiredFeatures.filter((f) => f !== feat)
        : [...desiredFeatures, feat]
    );
  };

  const handleFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const newFiles = Array.from(incoming).filter(
      (f) => f.size <= MAX_SIZE_MB * 1024 * 1024
    );
    setFiles([...files, ...newFiles].slice(0, MAX_FILES));
  };

  const removeFile = (idx: number) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  return (
    <div className="flex flex-col gap-5 px-6 py-5">
      <div>
        <p className="font-heading text-ts-base text-foreground mb-1">
          Now the good stuff
        </p>
        <p className="font-body text-ts-xs text-foreground/60">
          This is the main event — tell me everything.
        </p>
      </div>

      {/* Message */}
      <div>
        <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-1.5 block">
          Project details *
        </label>
        <textarea
          required
          maxLength={2000}
          rows={5}
          value={message}
          onChange={(e) => update("message", e.target.value)}
          className="w-full bg-transparent border-b border-border py-2 font-body text-ts-xs text-foreground placeholder:text-foreground/30 focus:border-primary focus:outline-none transition-colors resize-none"
          placeholder="Goals, scope, inspiration, anything that helps me understand your vision…"
        />
      </div>

      {/* Trade offer — shown when Dallas local + Barter/Trade selected */}
      {isDallasLocal && dallasOfferType === "Barter / Trade" && (
        <div>
          <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-2 block">
            Trade offer
          </label>
          <p className="font-body text-ts-2xs text-foreground/50 mb-3">
            Tell us what you'd like to trade — e.g. "We run a photography business. Build our site, you get a free 60 min session."
          </p>
          <textarea
            value={tradeOffer}
            onChange={(e) => setTradeOffer(e.target.value)}
            maxLength={500}
            rows={4}
            className="w-full bg-transparent border border-border p-3 font-body text-ts-xs text-foreground placeholder:text-foreground/30 focus:border-primary focus:outline-none transition-colors resize-none"
            placeholder="Describe your trade offer..."
          />
        </div>
      )}

      {/* Desired Functionality Chips — hidden for now, reworking per-service options */}
      {/* <AnimatePresence>
        {chips.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-2 block">
              Desired functionality
            </label>
            <div className="flex flex-wrap gap-1.5">
              {chips.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => toggleFeature(chip)}
                  className={`px-3 py-1.5 border font-body text-ts-2xs tracking-wide transition-colors ${
                    desiredFeatures.includes(chip)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
                  }`}
                >
                  {chip}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence> */}

      {/* Links */}
      <div>
        <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-1.5 block">
          Links / references
        </label>
        <textarea
          maxLength={1000}
          rows={2}
          value={links}
          onChange={(e) => update("links", e.target.value)}
          className="w-full bg-transparent border-b border-border py-2 font-body text-ts-xs text-foreground placeholder:text-foreground/30 focus:border-primary focus:outline-none transition-colors resize-none"
          placeholder="Figma, mockups, existing site URLs — one per line"
        />
      </div>

      {/* File Attachments */}
      <div>
        <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-2 block">
          Attachments
          <span className="text-muted-foreground/50 ml-1">
            (up to {MAX_FILES} files, {MAX_SIZE_MB}MB each)
          </span>
        </label>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          accept="image/*,.pdf,.doc,.docx,.xd,.sketch,.fig"
        />

        {files.length < MAX_FILES && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleFiles(e.dataTransfer.files);
            }}
            className="w-full border border-dashed border-border py-4 flex flex-col items-center gap-1.5 text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
          >
            <Upload size={16} />
            <span className="font-body text-ts-2xs">
              Drop files or click to browse
            </span>
          </button>
        )}

        {files.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {files.map((file, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 border border-border"
              >
                <Paperclip size={12} className="text-muted-foreground shrink-0" />
                <span className="font-body text-ts-2xs text-foreground truncate flex-1">
                  {file.name}
                </span>
                <button
                  type="button"
                  onClick={() => removeFile(idx)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StepFunPart;
